/**
 * sqlite3mt Control Worker runtime dispatch
 *
 * §96 §2.2, §5, §7.1, §11.3, §13, §14, §15, §16, R74–R78.
 *
 * This module owns the concrete Control Worker lifecycle and message loop that
 * ties together the already-extracted bootstrap, commit-registration, fault,
 * remote-head, sync-engine, and checkpoint handlers.
 *
 * Runtime guarantees:
 * - handler dispatch is sequenced through one promise tail (D1/L1)
 * - all state-affecting messages are generation-stamped and stale generations
 *   are discarded before dispatch (R75/L2)
 * - BOOTSTRAPPING never leaks normal traffic into RUNNING after the fact:
 *   messages received before bootstrap completion are processed while phase is
 *   still BOOTSTRAPPING and therefore discarded
 * - shutdown/restart abandons late bootstrap completions from older attempts,
 *   preventing stale async continuations from reopening the runtime (R74)
 */

import type { CheckpointDoneMessage, FaultRequestMessage } from './worker-bridge'
import type { WalCommitSyncMessage, PublicHead } from './types'

// ── §13 Remote head notification ─────────────────────────────────

/**
 * Remote head update from the transport layer.
 *
 * §13: When the SecureBlob transport detects a new remote head (via
 * push notification, polling, or CAS-mismatch response), it posts
 * this message to the Control Worker.
 *
 * R75: remote-head bridge traffic is generation-stamped. A fresh bootstrap
 * must be able to ignore late bridge events from the previous generation.
 */
export interface RemoteHeadUpdateMessage {
    type: 'remote-head-update'
    runtimeEpoch: bigint
    root: PublicHead['root']
    version: bigint
    /** Request sequence for ordering (from applyRemoteHeadIfNewer). */
    reqSeq: bigint
}

// ── Sync retry timer ────────────────────────────────────────────

/**
 * Internal sync retry timer fired.
 *
 * §19.2 + R74: the retry callback captures runtimeEpoch so that a timeout
 * created by an older generation cannot restart sync work in a fresh runtime.
 */
export interface SyncRetryFiredMessage {
    type: 'sync-retry-fired'
    runtimeEpoch: bigint
}

// ── Bootstrap completion ─────────────────────────────────────────

/**
 * Bootstrap completed successfully (sent from bootstrap orchestrator
 * to the runtime's own event loop, enabling normal operation).
 */
export interface BootstrapCompleteMessage {
    type: 'bootstrap-complete'
    runtimeEpoch: bigint
}

/**
 * Bootstrap failed fatally.
 */
export interface BootstrapFatalMessage {
    type: 'bootstrap-fatal'
    runtimeEpoch: bigint
    error: unknown
}

// ── Incoming message union ───────────────────────────────────────

/**
 * Union of all incoming messages to the Control Worker.
 *
 * Each message type maps to exactly one handler:
 *   wal-commit-sync   → onWalCommitSync (commit-registration.ts)
 *   fault             → handleFaultRequest (worker-bridge.ts)
 *   checkpoint-done   → handleCheckpointDone (worker-bridge.ts)
 *   remote-head-update → applyRemoteHeadIfNewer (remote-head.ts)
 *   sync-retry-fired  → maybeStartPushOrImport (sync-engine.ts)
 *   bootstrap-complete → transitions to normal operation
 *   bootstrap-fatal   → transitions to fatal state
 */
export type ControlWorkerIncomingMessage =
    | WalCommitSyncMessage
    | FaultRequestMessage
    | CheckpointDoneMessage
    | RemoteHeadUpdateMessage
    | SyncRetryFiredMessage
    | BootstrapCompleteMessage
    | BootstrapFatalMessage

// ── Lifecycle phases ─────────────────────────────────────────────

/**
 * Control Worker lifecycle phases.
 *
 * BOOTSTRAPPING: Initial state. CONTROL_READY = 0. No reads or commits.
 * RUNNING: Normal operation. CONTROL_READY = 1. All handlers active.
 * FATAL: Unrecoverable error. All gates closed. Only bootstrap can reset.
 */
export type ControlWorkerPhase = 'BOOTSTRAPPING' | 'RUNNING' | 'FATAL'

// ── Bootstrap/runtime hook contracts ────────────────────────────

/**
 * Completion outcome for a single bootstrap attempt.
 *
 * startBootstrap() returns the fresh runtimeEpoch synchronously, mirroring
 * §7.0 where the epoch is chosen before the first async bootstrap await.
 */
export type ControlWorkerBootstrapCompletion =
    | { kind: 'SUCCESS' }
    | { kind: 'FATAL'; error: unknown }

export interface ControlWorkerBootstrapRun {
    runtimeEpoch: bigint
    completion: Promise<ControlWorkerBootstrapCompletion>
}

/**
 * Concrete runtime dependencies.
 *
 * Each running-phase message maps to exactly one already-implemented handler.
 * The runtime owns lifecycle, phase gating, and generation filtering; the
 * hooks own protocol state mutations and side effects.
 */
export interface ControlWorkerRuntimeHooks {
    startBootstrap(previousRuntimeEpoch: bigint | null): ControlWorkerBootstrapRun
    onWalCommitSync(message: WalCommitSyncMessage): Promise<void>
    onFaultRequest(message: FaultRequestMessage): Promise<void>
    onFaultRequests?(messages: FaultRequestMessage[]): Promise<void>
    onCheckpointDone(message: CheckpointDoneMessage): Promise<void>
    onRemoteHeadUpdate(message: RemoteHeadUpdateMessage): Promise<void>
    onSyncRetryFired(message: SyncRetryFiredMessage): Promise<void>
    shutdown(): Promise<void> | void
}

// ── Runtime lifecycle interface ──────────────────────────────────

/**
 * Control Worker runtime lifecycle interface.
 *
 * This is the contract that the concrete runtime implementation must
 * satisfy. It defines the three lifecycle phases and the message
 * dispatch function.
 *
 * Invariants (enforced by the runtime):
 *   L1. Message handling is sequenced through one dispatch queue
 *   L2. Messages with stale runtimeEpoch are discarded before handler dispatch
 *   L3. wal-commit-sync / fault / checkpoint-done / remote-head-update /
 *       sync-retry-fired are only dispatched when phase === 'RUNNING'
 *   L4. bootstrap-fatal may transition the runtime to FATAL in any non-fatal phase
 *   L5. bootstrap-complete only opens normal operation from BOOTSTRAPPING
 *   L6. After phase transitions to FATAL, only init() can restart
 */
export interface ControlWorkerRuntime {
    /** Current runtime phase. */
    readonly phase: ControlWorkerPhase

    /** Current runtime epoch (changes on each bootstrap). */
    readonly runtimeEpoch: bigint

    /**
     * Initialize the runtime: start a fresh bootstrap generation.
     *
     * §7.1: Bootstrap restores from durable state, repairs ghost commits,
     * publishes initial SAB state, and transitions to RUNNING (or FATAL).
     *
     * After fatal or shutdown, init() may be called again for a fresh
     * generation bootstrap (R74). Calling init() while already RUNNING is an
     * error. Calling init() while BOOTSTRAPPING returns the in-flight attempt.
     */
    init(): Promise<void>

    /**
     * Dispatch an incoming message to the appropriate handler.
     *
     * Dispatch rules:
     *   - Stale epoch → discard
     *   - Phase BOOTSTRAPPING:
     *       bootstrap-complete → transition to RUNNING
     *       bootstrap-fatal → transition to FATAL
     *       other → discard
     *   - Phase RUNNING:
     *       wal-commit-sync → onWalCommitSync
     *       fault → handleFaultRequest
     *       checkpoint-done → handleCheckpointDone
     *       remote-head-update → applyRemoteHeadIfNewer bridge path
     *       sync-retry-fired → retry callback path
     *   - Phase FATAL:
     *       All messages → discard
     */
    handleMessage(message: ControlWorkerIncomingMessage): Promise<void>

    /**
     * Shut down the runtime cleanly.
     *
     * Publishes CONTROL_READY = 0, closes all gates, and wakes waiters.
     * After shutdown, only init() (fresh generation) can restart.
     */
    shutdown(): Promise<void>
}

// ── Dispatch helper: message type discriminant ───────────────────

/**
 * Discriminant key for ControlWorkerIncomingMessage.
 *
 * Every variant has a `type` field. This helper narrows the union.
 */
export type MessageDiscriminant = ControlWorkerIncomingMessage['type']

/**
 * Check whether a message type requires the runtime to be RUNNING.
 *
 * wal-commit-sync, fault, checkpoint-done, and remote-head-update
 * all require normal operation. sync-retry-fired is also a normal-op
 * message. Bootstrap and fatal messages are phase-transition messages.
 */
export function requiresRunningPhase(type: MessageDiscriminant): boolean {
    switch (type) {
        case 'wal-commit-sync':
        case 'fault':
        case 'checkpoint-done':
        case 'remote-head-update':
        case 'sync-retry-fired':
            return true
        case 'bootstrap-complete':
        case 'bootstrap-fatal':
            return false
        default:
            return false
    }
}

/**
 * Check whether a message carries a runtimeEpoch field.
 *
 * R75: every current ControlWorkerIncomingMessage variant is
 * generation-stamped.
 */
export function messageHasRuntimeEpoch(
    message: ControlWorkerIncomingMessage
): message is ControlWorkerIncomingMessage & { runtimeEpoch: bigint } {
    switch (message.type) {
        case 'wal-commit-sync':
        case 'fault':
        case 'checkpoint-done':
        case 'remote-head-update':
        case 'sync-retry-fired':
        case 'bootstrap-complete':
        case 'bootstrap-fatal':
            return true
        default:
            return false
    }
}

// ── Concrete runtime implementation ─────────────────────────────

export function createControlWorkerRuntime(
    hooks: ControlWorkerRuntimeHooks
): ControlWorkerRuntime {
    let phase: ControlWorkerPhase = 'BOOTSTRAPPING'
    let runtimeEpoch = 0n
    let bootstrapToken = 0
    let activeBootstrapToken: number | null = null
    let activeBootstrapPromise: Promise<void> | null = null
    let dispatchTail: Promise<void> = Promise.resolve()
    type FaultBatchEntry = {
        message: FaultRequestMessage
        resolve(): void
        reject(reason?: unknown): void
    }
    type FaultBatch = {
        entries: FaultBatchEntry[]
    }
    let activeFaultBatch: FaultBatch | null = null

    const enqueue = (op: () => Promise<void>): Promise<void> => {
        const next = dispatchTail.then(op, op)
        dispatchTail = next.then(noop, noop)
        return next
    }

    const handleBootstrapCompletion = async (
        message: BootstrapCompleteMessage | BootstrapFatalMessage
    ): Promise<void> => {
        // runtimeEpoch=0n is the pre-init sentinel; no bootstrap transition
        // message is valid until init() has chosen a fresh generation.
        if (runtimeEpoch === 0n || message.runtimeEpoch !== runtimeEpoch || phase === 'FATAL') {
            return
        }

        if (message.type === 'bootstrap-fatal') {
            phase = 'FATAL'
            return
        }

        if (phase === 'BOOTSTRAPPING') {
            phase = 'RUNNING'
        }
    }

    const dispatchMessage = async (
        message: ControlWorkerIncomingMessage
    ): Promise<void> => {
        if (!messageHasRuntimeEpoch(message) || message.runtimeEpoch !== runtimeEpoch) {
            return
        }

        if (message.type === 'bootstrap-complete' || message.type === 'bootstrap-fatal') {
            await handleBootstrapCompletion(message)
            return
        }

        if (phase !== 'RUNNING') {
            return
        }

        switch (message.type) {
            case 'wal-commit-sync':
                await hooks.onWalCommitSync(message)
                return
            case 'fault':
                await hooks.onFaultRequest(message)
                return
            case 'checkpoint-done':
                await hooks.onCheckpointDone(message)
                return
            case 'remote-head-update':
                await hooks.onRemoteHeadUpdate(message)
                return
            case 'sync-retry-fired':
                await hooks.onSyncRetryFired(message)
                return
            default:
                assertNeverMessage(message)
        }
    }

    const dispatchFaultBatch = async (
        batch: FaultBatch
    ): Promise<void> => {
        const messagesToDispatch: FaultRequestMessage[] = []
        const entriesToDispatch: FaultBatchEntry[] = []

        for (const entry of batch.entries) {
            const message = entry.message
            if (!messageHasRuntimeEpoch(message) || message.runtimeEpoch !== runtimeEpoch) {
                entry.resolve()
                continue
            }
            if (phase !== 'RUNNING') {
                entry.resolve()
                continue
            }
            messagesToDispatch.push(message)
            entriesToDispatch.push(entry)
        }

        if (messagesToDispatch.length === 0) {
            return
        }

        try {
            if (hooks.onFaultRequests != null) {
                await hooks.onFaultRequests(messagesToDispatch)
            } else {
                for (const message of messagesToDispatch) {
                    await hooks.onFaultRequest(message)
                }
            }
            for (const entry of entriesToDispatch) {
                entry.resolve()
            }
        } catch (error) {
            for (const entry of entriesToDispatch) {
                entry.reject(error)
            }
            throw error
        }
    }

    const enqueueFaultMessage = (
        message: FaultRequestMessage
    ): Promise<void> => {
        if (activeFaultBatch == null) {
            const batch: FaultBatch = { entries: [] }
            activeFaultBatch = batch
            const scheduled = enqueue(() => dispatchFaultBatch(batch))
            void scheduled.finally(() => {
                if (activeFaultBatch === batch) {
                    activeFaultBatch = null
                }
            }).catch(noop)
        }

        return new Promise<void>((resolve, reject) => {
            activeFaultBatch!.entries.push({ message, resolve, reject })
        })
    }

    const runtime: ControlWorkerRuntime = {
        get phase(): ControlWorkerPhase {
            return phase
        },

        get runtimeEpoch(): bigint {
            return runtimeEpoch
        },

        init(): Promise<void> {
            if (phase === 'RUNNING') {
                return Promise.reject(new Error('Control Worker runtime is already RUNNING'))
            }

            if (phase === 'BOOTSTRAPPING' && activeBootstrapPromise != null) {
                return activeBootstrapPromise
            }

            phase = 'BOOTSTRAPPING'
            const previousRuntimeEpoch = runtimeEpoch === 0n ? null : runtimeEpoch
            const token = ++bootstrapToken

            let run: ControlWorkerBootstrapRun
            try {
                run = hooks.startBootstrap(previousRuntimeEpoch)
            } catch (error) {
                phase = 'FATAL'
                return Promise.reject(error)
            }

            runtimeEpoch = run.runtimeEpoch

            const bootstrapPromise = run.completion.then(
                (completion) => {
                    if (activeBootstrapToken !== token) {
                        return
                    }

                    return enqueue(() => dispatchMessage(
                        completion.kind === 'SUCCESS'
                            ? {
                                type: 'bootstrap-complete',
                                runtimeEpoch: run.runtimeEpoch,
                            }
                            : {
                                type: 'bootstrap-fatal',
                                runtimeEpoch: run.runtimeEpoch,
                                error: completion.error,
                            }
                    )).then(() => {
                        if (completion.kind === 'FATAL') {
                            throw completion.error
                        }
                    })
                },
                (error) => {
                    if (activeBootstrapToken !== token) {
                        return
                    }

                    return enqueue(() => dispatchMessage({
                        type: 'bootstrap-fatal',
                        runtimeEpoch: run.runtimeEpoch,
                        error,
                    }))
                }
            ).finally(() => {
                if (activeBootstrapToken === token) {
                    activeBootstrapToken = null
                    activeBootstrapPromise = null
                }
            })

            activeBootstrapToken = token
            activeBootstrapPromise = bootstrapPromise
            return bootstrapPromise
        },

        handleMessage(message: ControlWorkerIncomingMessage): Promise<void> {
            if (message.type === 'fault') {
                return enqueueFaultMessage(message)
            }
            activeFaultBatch = null
            return enqueue(() => dispatchMessage(message))
        },

        shutdown(): Promise<void> {
            return enqueue(async () => {
                bootstrapToken += 1
                activeBootstrapToken = null
                activeBootstrapPromise = null
                phase = 'FATAL'
                await hooks.shutdown()
            })
        },
    }

    return runtime
}

function noop(): void {}

function assertNeverMessage(message: never): never {
    throw new Error(`unhandled Control Worker message: ${(message as { type?: string }).type ?? '<unknown>'}`)
}
